// react
import { useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import {
  createFileRoute,
  Link,
  useNavigate,
  useParams,
} from '@tanstack/react-router';

// material ui icons
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';

// material ui
import { Button, Card, Stack, Tooltip } from '@mui/material';

// api
import { useUnitsGetAsyncByidQuery } from '@/api/generated/recalc/endpoints/v1/units';

// shared components
import { WritableOnly } from '@/components/access-control';
import PageContainer from '@/components/layout/PageContainer';
import PageHeader from '@/components/layout/PageHeader';
import LocationMap from '@/components/maps/LocationMap';
import { DeleteButton } from '@/components/ui/button/DeleteButton';
import GatewayLoadingIndicator from '@/components/ui/progress/GatewayLoadingIndicator';

// feature-components
import DeviceTabs from '@/features/devices/components/shared/DeviceTabs';
import DeleteUnitDialog from '@/features/units/components/DeleteUnitDialog';
import UnitDetailHeader from '@/features/units/components/UnitDetailHeader';
import UnitImageSection from '@/features/units/components/UnitImageSection';

const TABS = {
  MAP: 0,
  IMAGE: 1,
};

export const Route = createFileRoute('/$accountId/_user/units/$unitId/')({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation('units-gateways-sensors');
  const { unitId } = useParams({ strict: false });
  const navigate = useNavigate();

  const {
    data: unit,
    isLoading,
    isFetching,
  } = useUnitsGetAsyncByidQuery({ id: Number(unitId) }, { skip: !unitId });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const handleDeleteUnit = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleted = () => {
    navigate({ to: '../' });
  };

  const hasLocation = unit?.Latitude != null && unit?.Longitude != null;
  const hasImage = unit?.ImageHash != null;

  // Keep the user's manual selection (null = no explicit choice yet).
  // Derive the active tab during render so it reflects loaded data immediately
  // without needing a useEffect to sync state.
  const [selectedTab, setSelectedTab] = useState(null);
  const activeTab =
    selectedTab ?? (hasLocation ? TABS.MAP : hasImage ? TABS.IMAGE : null);

  return (
    <PageContainer
      maxWidth="xl"
      sx={{ py: 2 }}
      data-testid="unit-detail-container"
    >
      <PageHeader title={t('units.unitDetails')}>
        <WritableOnly>
          <Tooltip
            title={t('units.editUnit', {
              name: unit?.Name || unit?.UnitId || '',
            })}
          >
            <Button
              component={Link}
              to={`edit`}
              size="small"
              variant="contained"
              startIcon={<EditRoundedIcon />}
            >
              {t('common:actions.edit', 'Edit')}
            </Button>
          </Tooltip>
          <Tooltip
            title={t('units.deleteUnit', {
              name: unit?.Name || unit?.UnitId || '',
            })}
          >
            <DeleteButton size="small" onClick={handleDeleteUnit}>
              {t('common:actions.delete', 'Delete')}
            </DeleteButton>
          </Tooltip>
        </WritableOnly>
      </PageHeader>
      <GatewayLoadingIndicator isLoading={isLoading || isFetching} />
      <Stack spacing={2} mb={2}>
        <UnitDetailHeader key={unit?.UnitId} unit={unit} />

        <DeviceTabs
          activeTab={activeTab}
          onTabChange={(_, newValue) => setSelectedTab(newValue)}
          tabs={[
            {
              label: t('units.location', 'Location'),
              testId: 'unit-tab-location',
              disabled: !hasLocation,
              icon: <LocationOnRoundedIcon fontSize="small" />,
            },
            {
              label: t('units.image', 'Image'),
              testId: 'unit-tab-image',
              disabled: !hasImage,
              icon: <ImageRoundedIcon fontSize="small" />,
            },
          ]}
        />

        {activeTab === TABS.MAP && hasLocation && (
          <Card>
            <LocationMap
              pins={[
                {
                  latitude: unit.Latitude,
                  longitude: unit.Longitude,
                  label: unit.Name,
                },
              ]}
            />
          </Card>
        )}
        {activeTab === TABS.IMAGE && hasImage && (
          <Card>
            <UnitImageSection unit={unit} t={t} />
          </Card>
        )}

        {/* {activeTab === TABS.CALIBRATION && <UnitCalibration unitId={unit?.UnitId} />} */}
      </Stack>
      <DeleteUnitDialog
        open={deleteDialogOpen}
        unit={unit ?? null}
        onClose={handleDeleteClose}
        onDeleted={handleDeleted}
      />
    </PageContainer>
  );
}
